import {
  decisionDetailSchema,
  decisionPageSchema,
  taskListSchema,
  type CreateDecisionRequest,
  type CreateDecisionTasksRequest,
  type DecisionStatus,
  type DecisionType,
  type DiscardDecisionRequest,
  type UpdateDecisionRequest,
  type VersionedRequest,
} from '@mfos/shared';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { apiGet, apiPatch, apiPost, withQuery } from '../../lib/api-client';
import { workspaceKey, workspacePath } from '../workspace/workspace.api';
import { useWorkspaceMutation } from '../workspace/workspace-mutation';

const DECISION_PAGE_SIZE = 25;

export interface DecisionFilters {
  readonly status?: DecisionStatus;
  readonly decisionType?: DecisionType;
  readonly categoryId?: string;
  readonly topicId?: string;
  readonly q?: string;
}

export const decisionsKey = (workspaceId: string) =>
  [...workspaceKey(workspaceId), 'decisions'] as const;
export const decisionKey = (workspaceId: string, decisionId: string) =>
  [...workspaceKey(workspaceId), 'decision', decisionId] as const;

function filterParams(filters: DecisionFilters) {
  return {
    status: filters.status,
    decisionType: filters.decisionType,
    categoryId: filters.categoryId,
    topicId: filters.topicId,
    q: filters.q,
  };
}

export function useDecisions(workspaceId: string, filters: DecisionFilters, enabled = true) {
  return useInfiniteQuery({
    queryKey: [...decisionsKey(workspaceId), 'list', filters] as const,
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam, signal }) =>
      apiGet(
        withQuery(workspacePath(workspaceId, 'decisions'), {
          ...filterParams(filters),
          cursor: pageParam,
          limit: DECISION_PAGE_SIZE,
        }),
        { schema: decisionPageSchema, signal },
      ),
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled,
  });
}

/** Same filters as the register; the browser downloads the CSV with the session cookie. */
export function decisionsExportPath(workspaceId: string, filters: DecisionFilters): string {
  return withQuery(workspacePath(workspaceId, 'decisions', 'export.csv'), filterParams(filters));
}

export function useDecision(workspaceId: string, decisionId: string, enabled = true) {
  return useQuery({
    queryKey: decisionKey(workspaceId, decisionId),
    queryFn: ({ signal }) =>
      apiGet(workspacePath(workspaceId, 'decisions', decisionId), {
        schema: decisionDetailSchema,
        signal,
      }),
    enabled,
  });
}

export function useCreateDecision(workspaceId: string) {
  return useWorkspaceMutation(workspaceId, (body: CreateDecisionRequest) =>
    apiPost(workspacePath(workspaceId, 'decisions'), body, decisionDetailSchema),
  );
}

export function useUpdateDecision(workspaceId: string, decisionId: string) {
  return useWorkspaceMutation(workspaceId, (body: UpdateDecisionRequest) =>
    apiPatch(workspacePath(workspaceId, 'decisions', decisionId), body, decisionDetailSchema),
  );
}

export function useFinalizeDecision(workspaceId: string, decisionId: string) {
  return useWorkspaceMutation(workspaceId, (body: VersionedRequest) =>
    apiPost(
      workspacePath(workspaceId, 'decisions', decisionId, 'finalize'),
      body,
      decisionDetailSchema,
    ),
  );
}

export function useDiscardDecision(workspaceId: string, decisionId: string) {
  return useWorkspaceMutation(workspaceId, (body: DiscardDecisionRequest) =>
    apiPost(
      workspacePath(workspaceId, 'decisions', decisionId, 'discard'),
      body,
      decisionDetailSchema,
    ),
  );
}

export function useCreateDecisionTasks(workspaceId: string, decisionId: string) {
  return useWorkspaceMutation(workspaceId, (body: CreateDecisionTasksRequest) =>
    apiPost(workspacePath(workspaceId, 'decisions', decisionId, 'tasks'), body, taskListSchema),
  );
}
